/**
 * Shared create/edit form for merchant stores.
 *
 * Unlike the location flow — which creates a row and then redirects to a Media
 * tab — both images are collected here and submitted with the rest: the
 * `logo_img_id` and `banner_img_id` columns are NOT NULL, so a store cannot
 * exist without them.
 */

import React, { useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ChromePicker } from 'react-color';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import PageContainer from '../Redesing/page-container';
import CardView from '../Redesing/card-view';
import FormItem from '../Redesing/form-item';
import IconButtonMenu from '../Redesing/icon-button-menu';
import CustomAsyncToast from '../Redesing/custom-async-toast';
import { MovaticFieldGroupText } from '../Redesing/movatic-field-group';
import MerchantStoreLocationPicker from './MerchantStoreLocationPicker';
import { merchantStoreMarker } from '../mapMarker';
import MerchantStoreImageField from './MerchantStoreImageField';
import { history } from '../../utils';
import {
  useCreateMerchantStoreMutation,
  useUpdateMerchantStoreMutation,
} from '../../hooks/merchantStores/mutation/V2';
import type { IMerchantStore } from '../../hooks/merchantStores/query/V2';

export type MerchantStoreFormMode = 'create' | 'edit';

export interface MerchantStoreFormProps {
  mode: MerchantStoreFormMode;
  /** Required when creating. */
  organizationId?: string;
  /** Existing store when editing. */
  initialValues?: IMerchantStore;
  merchantStoreId?: string;
  isLoading?: boolean;
  cancelHref: string;
  successHref: string;
  title?: string;
}

const DEFAULT_PRIMARY = '#38556d';
const DEFAULT_SECONDARY = '#ffffff';

const MerchantStoreForm: React.FC<MerchantStoreFormProps> = ({
  mode,
  organizationId,
  initialValues,
  merchantStoreId,
  isLoading = false,
  cancelHref,
  successHref,
  title,
}) => {
  const isEditMode = mode === 'edit';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [name, setName] = useState(initialValues?.name ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(initialValues?.website_url ?? '');
  const [lat, setLat] = useState(
    initialValues?.lat != null ? String(initialValues.lat) : ''
  );
  const [lng, setLng] = useState(
    initialValues?.lng != null ? String(initialValues.lng) : ''
  );
  const [primaryColor, setPrimaryColor] = useState(
    initialValues?.primary_color || DEFAULT_PRIMARY
  );
  const [secondaryColor, setSecondaryColor] = useState(
    initialValues?.secondary_color || DEFAULT_SECONDARY
  );
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [bannerBase64, setBannerBase64] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The map marker doubles as a preview of the merchant card in the app, so it
  // follows the logo and primary color as they are edited rather than waiting
  // for a save.
  const markerIcon = useMemo(
    () => merchantStoreMarker(logoPreviewUrl ?? initialValues?.logo_url, primaryColor),
    [logoPreviewUrl, initialValues?.logo_url, primaryColor]
  );

  const { createMerchantStore, isLoading: isCreating } = useCreateMerchantStoreMutation();
  const { updateMerchantStore, isLoading: isUpdating } = useUpdateMerchantStoreMutation();
  const isSubmitting = isCreating || isUpdating;

  const validate = () => {
    const next: Record<string, string> = {};

    if (!name.trim()) next.name = 'A name is required';

    const numericLat = parseFloat(lat);
    if (lat === '' || Number.isNaN(numericLat)) next.lat = 'Latitude is required';
    else if (numericLat > 90 || numericLat < -90)
      next.lat = 'Latitude must be between -90 and 90';

    const numericLng = parseFloat(lng);
    if (lng === '' || Number.isNaN(numericLng)) next.lng = 'Longitude is required';
    else if (numericLng > 180 || numericLng < -180)
      next.lng = 'Longitude must be between -180 and 180';

    // Both columns are NOT NULL, so a new store needs both images up front.
    if (!isEditMode && !logoBase64) next.logo = 'A logo is required';
    if (!isEditMode && !bannerBase64) next.banner = 'A banner is required';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    const shared = {
      name: name.trim(),
      websiteUrl: websiteUrl.trim() || null,
      lat: parseFloat(lat),
      lng: parseFloat(lng),
      primaryColor,
      secondaryColor,
    };

    const result = await CustomAsyncToast({
      promise: () => {
        const call = isEditMode
          ? updateMerchantStore(merchantStoreId ?? '', {
              ...shared,
              // Omitted means "keep the current image".
              ...(logoBase64 ? { logoBase64 } : {}),
              ...(bannerBase64 ? { bannerBase64 } : {}),
            })
          : createMerchantStore({
              ...shared,
              organizationId: organizationId ?? '',
              logoBase64: logoBase64 ?? '',
              bannerBase64: bannerBase64 ?? '',
            });

        return call.then((res) => {
          if (!res.isSuccess) {
            throw new Error(
              res.errors[0]?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} merchant location`
            );
          }
          return res.data;
        });
      },
      successMessage: () =>
        `Merchant location ${isEditMode ? 'updated' : 'created'} successfully!`,
      errorMessage: `There was an error ${
        isEditMode ? 'updating' : 'creating'
      } the merchant location.`,
      loadingMessage: `${isEditMode ? 'Updating' : 'Creating'} merchant location...`,
    });

    if (result) {
      history.push(
        isEditMode ? successHref : `/branded-app/merchant-locations/${result.id}/general`
      );
    }
  };

  const colorPicker = (label: string, value: string, onChange: (hex: string) => void) => (
    <Stack direction="column" spacing={1}>
      <Typography variant="subtitle2">{label}</Typography>
      <div className={isDarkMode ? 'dark-mode-chrome-picker' : 'none'}>
        <ChromePicker
          color={value}
          onChangeComplete={(colorEvent: { hex: string }) => onChange(colorEvent.hex)}
        />
      </div>
    </Stack>
  );

  return (
    <PageContainer isLoading={isLoading}>
      <CardView
        title={title ?? (isEditMode ? 'Edit Merchant Location' : 'Add Merchant Location')}
        headerActions={
          <IconButtonMenu
            buttons={[
              { label: 'Cancel', onClick: () => history.push(cancelHref) },
              {
                label: 'Save',
                onClick: submit,
                dataId: 'save-button',
                startIcon: <SaveIcon />,
                disabled: isSubmitting,
              },
            ]}
          />
        }
        content={
          <Stack px={2} pb={2}>
            <MovaticFieldGroupText
              id="name"
              name="name"
              label="Name"
              description="Shown on the merchant card in the rider app."
              value={name}
              hasError={Boolean(errors.name)}
              errorMessage={errors.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
            />

            <MovaticFieldGroupText
              id="websiteUrl"
              name="websiteUrl"
              label="Website"
              description="Opened in the app when a rider taps the merchant card."
              value={websiteUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setWebsiteUrl(e.target.value)
              }
            />

            <MerchantStoreLocationPicker
              lat={lat}
              lng={lng}
              onChange={({ lat: nextLat, lng: nextLng }) => {
                setLat(nextLat);
                setLng(nextLng);
              }}
              latError={errors.lat}
              lngError={errors.lng}
              icon={markerIcon}
            />

            <MerchantStoreImageField
              label="Logo"
              description="Square. Drawn as a small circle outlined in the primary color."
              imageType="icon"
              currentUrl={initialValues?.logo_url}
              onChange={(value) => {
                setLogoBase64(value?.base64 ?? null);
                setLogoPreviewUrl(value?.previewUrl ?? null);
              }}
              hasError={Boolean(errors.logo)}
              errorMessage={errors.logo}
            />

            <MerchantStoreImageField
              label="Banner"
              description="Wide (3:1). Fills the top of the merchant card."
              imageType="banner"
              currentUrl={initialValues?.banner_url}
              onChange={(value) => setBannerBase64(value?.base64 ?? null)}
              hasError={Boolean(errors.banner)}
              errorMessage={errors.banner}
            />

            <FormItem
              label="Colors"
              description="Primary outlines the logo in the app; secondary is used alongside it."
              content={
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {colorPicker('Primary', primaryColor, setPrimaryColor)}
                  {colorPicker('Secondary', secondaryColor, setSecondaryColor)}
                </Stack>
              }
            />
          </Stack>
        }
      />
    </PageContainer>
  );
};

export default MerchantStoreForm;
